import { useState, useEffect } from 'react';
import { Card, Chip, Typography, Stack, TextField, Grid, Button, Box, Tooltip, IconButton, Badge, Avatar, useTheme, useMediaQuery } from '@mui/material';
import Swal from 'sweetalert2';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { DataGrid,useGridApiRef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from '../../utils/dateUtils';
import { useForm, FormProvider, Controller, set } from 'react-hook-form';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import { ProviderServices } from './CrudServices';
import NoRowsLayout from '../../components/Layout/NoRowsLayout';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import calculateAge from '../../utils/ageCalculation';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import { PermissionGuard } from '../../components/PermissionGuard';
import { Helpers } from '../../services/Helpers';
import { CheckCircle, Warning, Pending, ArrowBack, Map } from '@mui/icons-material';
import Loading from '../../components/Loader/Loading';
import PageHeaderLayout from '../../components/Layout/PageHeaderLayout';
import ExportAll from './filterAndExport/ExportAll';
import MultiColumnFilter from '../../components/Filter/MultiColumnFilter';
import { useDispatch, useSelector } from 'react-redux';
import QuickSearchTool from '../../components/QuickSearch/QuickSearchTool';

const statusConfigs = [
  {
    label: 'Review Pending',
    value: '0',
    color: 'warning',
    colorCode: '#ed6c02',
    icon: <Pending sx={{ fontSize: '0.25rem' }} />,
    tooltip: 'Account is under review'
  },
  {
    label: 'Approved',
    value: '1',
    color: 'success',
    colorCode: '#2e7d32',
    icon: <CheckCircle sx={{ fontSize: '0.25rem' }} />,
    tooltip: 'Account has been Approved Already'
  },
  {
    label: 'Rejected',
    value: '2',
    color: 'error',
    colorCode: '#d32f2f',
    icon: <Warning sx={{ fontSize: '0.25rem' }} />,
    tooltip: 'Account has been rejected'
  }
];

export default function IncompleteProfile() {

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Grid API Reference
  const navigate = useNavigate();

  /************************  Table Data State Manage **************************** */

  const columns = [
    {
      field: 'name',
      headerName: 'Profile',
      flex: 1.75,
      headerAlign: 'center',
      sortable: false,
      valueGetter: (params) => {
        const id = params.row.id || '';
        const fullName = `${params.row.UserProfile?.firstName || ''} ${params.row.UserProfile?.middleName || ''} ${params.row.UserProfile?.lastName || ''}`;
        const email = params.row.UserProfile?.email || '';
        const age = calculateAge(params.row.UserProfile?.dob) || '';
        const mobile = params.row.mobileNumber || '';
        const reviewStatus = statusConfigs[params.row.reviewStatus]?.label || '';

        // Combine all profile information into a single searchable string
        return `${fullName} ${email} ${age} ${mobile} ${id} ${reviewStatus}`.trim();
      },
      renderCell: (params) => (


        <Box sx={{ p: 1, display: { xs: 'block', sm: 'flex' }, justifyContent: 'flex-start', width: '100%' }}>
          <Stack
            direction="column"
            spacing={1}
            justifyContent="flex-start"
            sx={{ mt: 1, width: '100%' }}
          >
            <Box sx={{ display: { xs: 'block', sm: 'flex' }, gap: '5%', justifyContent: 'flex-start', width: '100%' }}>
              <Badge color={params.row.isVerified ? 'success' : 'error'} badgeContent=" " variant='dot' sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 'fit-content', height: 'fit-content' }}>
                <Avatar src={`${Helpers.ImagePathUrl()}/profile/sm/${params.row.UserProfile?.profileImage}`} />
              </Badge>
              <Box>
                <Typography
                  onClick={(e) => window.open(`/admin/expert-detail/${params.row.id}`, '_blank')}
                  variant="body2"
                  sx={{
                    cursor: 'pointer', fontSize: '.65rem', fontWeight: 500,
                    //on hover color
                    '&:hover': {
                      color: 'warning.main'
                    }
                  }}
                  color={params.row.UserProfile?.firstName ? '#1976d2' : '#d32f2f'}
                >
                  {`${params.row.UserProfile?.firstName || ''} ${params.row.UserProfile?.middleName || ''} ${params.row.UserProfile?.lastName || ''}`}
                  {!params.row.UserProfile?.firstName && 'Incomplete Profile'}
                  <Box component={'span'} sx={{ fontSize: '.55rem', fontWeight: 600, color: '#382cd5' }}>({params.row.id})</Box>
                </Typography>
                <Chip
                  icon={statusConfigs[params.row.reviewStatus]?.icon}
                  label={statusConfigs[params.row.reviewStatus]?.label}
                  color={statusConfigs[params.row.reviewStatus]?.color}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.5rem', height: 'fit-content', '& .MuiChip-icon': { fontSize: '0.65rem' } }}
                />
                <Box>
                  <Typography variant="caption" sx={{ fontSize: '0.5rem' }}>{params.row.UserProfile?.email || 'N/A'}</Typography>
                  <Typography sx={{ fontSize: '0.5rem' }}> <Box component={'span'} sx={{ fontWeight: 600 }}>Mobile:</Box>{` ${params.row.mobileNumber}`}</Typography>
                  <Typography sx={{ fontSize: '0.5rem' }}><Box component={'span'} sx={{ fontWeight: 600 }}>Age: </Box>{`${calculateAge(params.row.UserProfile?.dob) || 'N/A'}`}</Typography>
                </Box>
              </Box>

            </Box>
          </Stack>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      headerAlign: 'center', sortable: false,
      filterable: false,
      flex: .6,
      valueGetter: (params) => {
        const isActive = params.row.isActive ? 'Active' : 'Inactive';
        const isVerified = params.row.isVerified ? 'Verified' : 'non-verified';
        return `${isActive} ${isVerified}`;
      },
      renderCell: (params) => (
        <Box sx={{ width: 'max-content', m: 'auto' }}>
          <Chip
            label={params.row.isActive ? 'Active' : 'Inactive'}
            color={params.row.isActive ? 'success' : 'error'}
            size="small"
            sx={{ fontSize: '0.5rem', height: 'fit-content', py: '5%' }}
          />
        </Box>
      ),
    },
    {
      field: 'verification',
      headerName: 'Verification',
      headerAlign: 'center', sortable: false,
      filterable: false,
      flex: .75,
      valueGetter: (params) => {
        const isKycVerified = params.row.isKycVerified ? 'KYC' : '';
        const isPanVerified = params.row.isPanVerified ? 'PAN' : '';
        const isAadhharVerified = params.row.isAadhharVerified ? 'Aadhaar' : '';
        return `${isKycVerified} ${isPanVerified} ${isAadhharVerified}`;
      },
      renderCell: (params) => (
        <Box sx={{ width: '60%', m: 'auto' }}>
          <Box sx={{ display: 'flex', gap: 1, my: '5%', alignItems: 'center' }}>
            <IconButton size='small' sx={{ p: 0 }}>
              {
                params.row.isKycVerified ?
                  <CheckCircleIcon color='success' sx={{ fontSize: '0.65rem' }} />
                  : <CancelIcon color='error' sx={{ fontSize: '0.65rem' }} />
              }
            </IconButton>
            <Typography variant="body2" sx={{ fontSize: '0.5rem', width: '75%', fontWeight: '600', color: '#3e3e3e' }}>KYC</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, my: '5%', alignItems: 'center' }}>
            <IconButton size='small' sx={{ p: 0 }}>
              {
                params.row.isPanVerified ?
                  <CheckCircleIcon color='success' sx={{ fontSize: '0.65rem' }} />
                  : <CancelIcon color='error' sx={{ fontSize: '0.65rem' }} />
              }
            </IconButton>
            <Typography variant="body2" sx={{ fontSize: '0.5rem', width: '75%', fontWeight: '600', color: '#3e3e3e' }}>PAN</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, my: '5%', alignItems: 'center' }}>
            <IconButton size='small' sx={{ p: 0 }}>
              {
                params.row.isAadhharVerified ?
                  <CheckCircleIcon color='success' sx={{ fontSize: '0.65rem' }} />
                  : <CancelIcon color='error' sx={{ fontSize: '0.65rem' }} />
              }
            </IconButton>
            <Typography variant="body2" sx={{ fontSize: '0.5rem', width: '75%', fontWeight: '600', color: '#3e3e3e' }}>AADHAR</Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'categories',
      headerName: 'Categories',
      headerAlign: 'center', sortable: false,
      filterable: false,
      flex: 1.25,
      valueGetter: (params) => {
        return params.row.providerCat.map(cat => cat.proServCat?.title).join(', ');
      },
      renderCell: (params) => {


        // Check if this specific row is expanded
        const isRowExpanded = expandedRowIds[params.row.id];

        return (
          <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '1%' }}>
            {params.row.providerCat.length > 0 && params.row.providerCat.map((cat, index) => (
              <Box key={index} >
                {/* Always show first 3 categories */}
                {index < 3 && (
                  <>
                    <Chip label={cat.proServCat?.title} size="small" sx={{ my: '1%', fontSize: '0.5rem', height: 'fit-content', py: '3%' }} />
                    {/* <br/> */}
                  </>
                )}

                {/* Show additional categories when row is expanded */}
                {isRowExpanded && index >= 3 && (
                  <>
                    <Chip label={cat?.proServCat?.title} size="small" sx={{ my: '1%', fontSize: '0.5rem', height: 'fit-content', py: '3%' }} />
                    {/* <br/> */}
                  </>
                )}
              </Box>
            ))}

            {/* Show more/less button if more than 4 categories */}
            {params.row?.providerCat.length > 4 && (
              <Box sx={{ width: '100%', textAlign: 'end' }}>
                <Button
                  size='small'
                  sx={{ fontSize: '.45rem' }}
                  onClick={() => toggleRowExpansion(params.row.id)}
                >
                  {isRowExpanded ? 'view less' : `view ${params.row.providerCat.length - 3} more`}
                </Button>
              </Box>
            )}

            {/* Show N/A if no categories */}
            <Typography sx={{
              fontSize: '0.5rem',
              textAlign: 'center',
              width: '100%'
            }}>
              {params.row.providerCat.length === 0 && 'N/A'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'businessDetail',
      headerName: 'Business Details', headerAlign: 'center', flex: 1, sortable: false, filterable: false,
      renderCell: (params) => (
        <Box sx={{ width: '95%', m: 'auto' }}>
          <Box sx={{ display: 'flex', gap: 1, my: '5%', justifyContent: 'flex-start' }}>
            <IconButton size='small' sx={{ p: 0 }}>{(params.row.UserBusinessDetail?.gstNumber || params.row.UserBusinessDetail?.panNumber || params.row.UserBusinessDetail?.aadharNumber) ? <CheckCircleIcon color='success' sx={{ fontSize: '0.65rem' }} /> : <CancelIcon color='error' sx={{ fontSize: '0.65rem' }} />}</IconButton>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#3e3e3e', fontSize: '0.5rem' }}>Business Profile</Typography>
          </Box>
          {/* // } */}
          <Box sx={{ display: 'flex', gap: 1, my: '5%', alignItems: 'center', }}>
            <IconButton size='small' sx={{ p: 0 }}>{params.row.ProviderServices.length > 0 ? <CheckCircleIcon color='success' sx={{ fontSize: '0.65rem' }} /> : <CancelIcon color='error' sx={{ fontSize: '0.65rem' }} />}</IconButton>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#3e3e3e', fontSize: '0.5rem' }}>Services {params.row.ProviderServices.length > 0 && <Box component={'span'}>({params.row.ProviderServices.length})</Box>}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, my: '5%', alignItems: 'center', }}>
            <IconButton size='small' sx={{ p: 0 }}>{params.row.providerSlots.length > 0 ? <CheckCircleIcon color='success' sx={{ fontSize: '0.65rem' }} /> : <CancelIcon color='error' sx={{ fontSize: '0.65rem' }} />}</IconButton>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#3e3e3e', fontSize: '0.5rem' }}>Availability</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, my: '5%', alignItems: 'center', }}>
            <IconButton size='small' sx={{ p: 0 }}>{params.row.UserBankDetail?.ifscCode ? <CheckCircleIcon color='success' sx={{ fontSize: '0.65rem' }} /> : <CancelIcon color='error' sx={{ fontSize: '0.65rem' }} />}</IconButton>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#3e3e3e', fontSize: '0.5rem' }}>Bank Details</Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'createdAt', headerName: 'Created At', headerAlign: 'center', flex: 0.75, filterable: false, sortingOrder: ['asc', 'desc'],
      valueGetter: (params) => {
        return formatDateTime(params.row.createdAt);
      },
      renderCell: (params) => (
        <Typography variant="body2" sx={{ width: '100%', textAlign: 'center', color: '#3e3e3e', fontSize: '0.6rem' }}>{formatDateTime(params.row.createdAt)}</Typography>
      ),
    },
    {
      field: 'updatedAt', headerName: 'Updated At', headerAlign: 'center', flex: 0.75, filterable: false, sortingOrder: ['asc', 'desc'],
      valueGetter: (params) => {
        return formatDateTime(params.row.updatedAt, 'date');
      },
      renderCell: (params) => (
        <Typography variant="body2" sx={{ width: '100%', textAlign: 'center', color: '#3e3e3e', fontSize: '0.6rem' }}>{params.row.updatedAt ? formatDateTime(params.row.updatedAt) : ''}</Typography>
      ),
    },
    {
      field: "actions", headerName: "Action", sortable: false, filterable: false, flex: .5, renderCell: (params) => {

        return (
          <Stack spacing={2} direction="row" justifyContent={'center'} width={'100%'}>
            <PermissionGuard feature='service-expert' permission='canEdit'>
              <BorderColorOutlinedIcon
                style={{
                  fontSize: "20px",
                  color: "#ff3700",
                  cursor: "pointer",
                }}
                className="cursor-pointer"
                onClick={(e) => window.open(`/admin/userProfile/${params.row.id}`, '_blank')}
              />
            </PermissionGuard>
          </Stack>
        );
      },
      headerAlign: 'center',
    },
  ];

  // Initialize paginationModel state with potential localStorage backup
  const [paginationModel, setPaginationModel] = useState(
    {
      page: 0,
      pageSize: 50,
      filters: {
        searchKey: "",
        sortBy: 'updatedAt',
        sortType: 'desc',
        filterOptions: [
          {
            "fieldName": "profile",
            "operator": "equals",
            "value": "incomplete"
          },
          // {
          //   "fieldName": "isTester",
          //   "operator": "equals",
          //   "value": "false",
          //   "andOr": "and"
          // }
        ],
      }
      // };
    });

  // State management
  const [allRowCount, setAllRowCount] = useState(0);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Data fetching effect
  useEffect(() => {
    const getData = async () => {
      // Skip initial unnecessary fetch
      if (isInitialLoad) {
        setIsInitialLoad(false);
        return;
      }

      try {
        setLoading(true);
        const listResponse = await ProviderServices.fetchProviderList({
          page: paginationModel.page,
          limit: paginationModel.pageSize,
          filters: {
            searchKey: paginationModel.filters.searchKey,
            // status: paginationModel.filters.status,
            sortBy: paginationModel.filters.sortBy,
            sortType: paginationModel.filters.sortType,
            filterOptions: paginationModel.filters.filterOptions || []
          }
        },'incomplete');

        if (listResponse.success == '1') {
          console.log("no. of items:", listResponse.data.list?.length);
          setList(listResponse.data.list);

          setAllRowCount(listResponse.data.totalItems);
        } else {
          // console.log()
          // Swal.fire(listResponse.message, '', 'error');
          setList([]);
          setAllRowCount(0);
        }
      } catch (error) {
        Swal.fire(error.message, '', 'error');
      }
      setLoading(false);
    };
    getData();
  }, [paginationModel, isInitialLoad]);

  // Pagination change handler
  const handlePaginationChange = (newPagination) => {
    setPaginationModel(prev => ({
      ...prev,
      page: newPagination.page,
      pageSize: newPagination.pageSize
    }));
  };


  // Form handling for filters
  const filterFormMethod = useForm();
  const { control, handleSubmit, reset, setValue, getValues, formState: { errors }, watch } = filterFormMethod;

  // Filter submission handler
  function onSubmit(data) {
    const filtersData = getValues();
    setPaginationModel(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        searchKey: filtersData.searchKey,
      }
    }));
  }

  // Clear filters handler
  const clearFilters = () => {
    reset();
    setPaginationModel(prev => ({
      ...prev,
      filters: {
        searchKey: "",
        // status: 1,
        sortBy: 'createdAt',
        sortType: 'desc',
        filterOptions: [
            {
            "fieldName": "profile",
            "operator": "equals",
            "value": "incomplete"
          },
        ]
      }
    }));

    // Trigger clear for MultiColumnFilter
    setClearMultiFilter(true);

    // Reset the clear trigger after a short delay
    setTimeout(() => setClearMultiFilter(false), 100);
  };

  /************************  Table Column Manage **************************** */

  // State to track expanded rows
  const [expandedRowIds, setExpandedRowIds] = useState({});

  // Toggle expansion for expert categories of a specific row
  const toggleRowExpansion = (rowId) => {
    setExpandedRowIds(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  /************************  Advance Filter **************************** */


  const [clearMultiFilter, setClearMultiFilter] = useState(false);

  const filterableColumns = [
    {
      key: 'profile',
      label: 'Profile',
      type: 'select',
      options: [
        { value: 'complete', label: 'complete' },
        { value: 'incomplete', label: 'in-complete' }
      ]
    },
    {
      key: 'name',
      label: 'Name',
      type: 'text'
    },
    {
      key: 'mobileNumber',
      label: 'Mobile',
      type: 'number'
    },
    {
      key: 'email',
      label: 'Email',
      type: 'email'
    },
    {
      key: 'reviewStatus',
      label: 'Review Status',
      type: 'select',
      options: [
        { value: '0', label: 'Review Pending' },
        { value: '1', label: 'Approved' },
        { value: '2', label: 'Rejected' }
      ]
    },
    {
      key: 'isActive',
      label: 'Account Status',
      type: 'select',
      options: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' }
      ]
    },
    {
      key: 'isKycVerified',
      label: 'KYC Verified',
      type: 'select',
      options: [
        { value: 'true', label: 'Verified' },
        { value: 'false', label: 'Not Verified' }
      ]
    },
    {
      key: 'isPanVerified',
      label: 'PAN Verified',
      type: 'select',
      options: [
        { value: 'true', label: 'Verified' },
        { value: 'false', label: 'Not Verified' }
      ]
    },
    {
      key: 'isAadhharVerified',
      label: 'Aadhaar Verified',
      type: 'select',
      options: [
        { value: 'true', label: 'Verified' },
        { value: 'false', label: 'Not Verified' }
      ]
    },
    {
      key: 'isVerified',
      label: 'Is MobileNumber Verified',
      type: 'select',
      options: [
        { value: 'true', label: 'Verified' },
        { value: 'false', label: 'Not Verified' }
      ]
    },
    {
      key: 'createdAt',
      label: 'Created Date',
      type: 'date'
    },
    {
      key: 'isTester',
      label: 'Is Tester',
      type: 'select',
      options: [
        { value: 'true', label: 'True' },
        { value: 'false', label: 'False' }
      ]
    }
  ];

  const handleApplyFilter = (filterData) => {
    // Process filter data
    console.log(filterData);
    setPaginationModel({
      ...paginationModel,
      page: 0, // Reset to first page when applying new filters
      filters: {
        ...paginationModel.filters,
        // Convert MultiColumnFilter options to a format your backend can understand
        filterOptions: filterData.filterOption.filter(filter => filter.fieldName && filter.value) // Remove empty filters
      }
    });
  }

  /************* quick search related **************/
  const [quickFilterText, setQuickFilterText] = useState('');
  // Properly structured filter model
  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: []
  });

  // Update filter model when quick filter text changes
  useEffect(() => {
    setFilterModel(prevModel => ({
      ...prevModel,
      quickFilterValues: quickFilterText ? [quickFilterText] : []
    }));
  }, [quickFilterText]);

  /******************* Sorting model  ******************************/
  const [sortModel, setSortModel] = useState([
    {
      field: paginationModel.filters.sortBy,
      sort: paginationModel.filters.sortType,
    },
  ]);

  const handleSortingChange = (newSortModel) => {
    console.log("new sort model", newSortModel);
    if (newSortModel && newSortModel.length > 0) {
      setSortModel(newSortModel);
      setPaginationModel(prev => ({
        ...prev,
        filters: {
          ...prev.filters,
          sortBy: newSortModel[0].field,
          sortType: newSortModel[0].sort
        }
      }));
    }
    // else{
    //   setSortModel([{
    //     field: 'createdAt',
    //     sort: 'asc',
    //   }]);
    //   setPaginationModel(prev => ({
    //     ...prev,
    //     filters: {
    //       ...prev.filters,
    //       sortBy: 'createdAt',
    //       sortType: 'asc'
    //     }
    //   }));
    // }
  }
  return (
    <>
      {loading && <Loading />}
      
      <Card sx={{ minWidth: "100%", marginBottom: ".5%" }}>
        
      </Card>

      <Card sx={{ overflow: "auto" }}>
        <Box sx={{ width: "100%", display: "table", tableLayout: "fixed", backgroundColor: "#fff" }}>
          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', px: '1%' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <MultiColumnFilter
                filterableColumns={filterableColumns}
                onApplyFilter={handleApplyFilter}
                externalClear={clearMultiFilter}
                defaultFilterOptions={[
                  {
                    "fieldName": "profile",
                    "operator": "equals",
                    "value": "incomplete"
                  }
                ]}
                alwaysKeepDefaultFilterOption={true}
              />
              <ExportAll
                paginationModel={paginationModel}
                columns={columns}
                profile = 'incomplete'
              />
            </Box>
            {/* <QuickSearchTool filterModel={filterModel} setFilterModel={setFilterModel} />             */}
            <Box sx={{width:{xs:'75%',sm:'40%',md:'30%',lg:'25%'}}}>
              {/* <QuickSearchTool filterModel={filterModel} setFilterModel={setFilterModel} /> */}
              <FormProvider {...filterFormMethod}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Grid container spacing={1} sx={{ p: '.5%' }}>
                    <Grid item xs={9} lg={8}>
                      <Controller
                        name="searchKey"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            {...field}
                            placeholder="Search by name,mobile,email "
                            // label="Search"
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                paddingLeft: '0px',
                              }
                            }}
                            InputProps={{  // Changed from slotProps to InputProps
                              inputProps: {
                                style: {
                                  height: '0.6rem',
                                  textTransform: 'uppercase',
                                  fontSize: 'x-small'
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={3} lg={4}>
                      <Tooltip title="Search" sx={{ ml: 1, p: 0 }}>
                        <IconButton
                          size='small'
                          color="primary"
                          type="submit"
                          sx={{ border: "1px solid", borderRadius: "7px", padding: "1%", ml: 1 }}
                        >
                          <SearchIcon sx={{ fontSize: '.85rem' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Clear All Filters">
                        <IconButton
                          size='small'
                          color="primary"
                          sx={{ marginLeft: "10px", border: "1px solid", color: "#f00", borderRadius: "7px", padding: "1%" }}
                          onClick={clearFilters}
                        >
                          <RestartAltIcon sx={{ fontSize: '.85rem' }} />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </form>
              </FormProvider>
            </Box>
            
          </Box>
          <DataGrid
            rows={list}
            columns={columns.map((col) => ({
              ...col,
              // flex: isSmallScreen && (col.flex || 1), // Ensure columns resize dynamically
              minWidth: isSmallScreen && (col.minWidth || 180), // Set a minimum width to avoid overlap
            }))}
            rowCount={allRowCount}
            pageSizeOptions={[5, 10, 20, 50, 100]}
            paginationModel={paginationModel}
            paginationMode="server"
            onPaginationModelChange={handlePaginationChange}
            filterModel={filterModel}


            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortingChange}
            disableMultiSort={true}

            loading={loading}
            disableColumnMenu
            disableRowSelectionOnClick
            slots={{
              // toolbar: GridToolbar,   
              loadingOverlay: Loading,
              noRowsOverlay: NoRowsLayout,
            }}
            autoHeight
            getRowHeight={() => 'auto'}
            sx={{
              lineHeight: 1,
              borderTopLeftRadius:0,
              borderLeft:0,
              '& .MuiDataGrid-cell': {
                // first cell borderLeft:0
                
                border: 1,
                borderRight: 0,
                borderTop: 0,
                borderColor: 'lightgrey',
                display: 'flex',
                alignItems: 'center',
                // justifyContent: 'center',
              },
              '& .MuiDataGrid-columnHeaders': {
                borderBottom: '2px solid rgba(224, 224, 224, 1)',
                // borderTop: '2px solid rgba(224, 224, 224, 1)',
                fontWeight: 600,
                fontSize: '0.75rem',
                p: 0,
                // height: 'fit-content',
                minHeight: '0 !important',
                height: '2rem'
              },
              '& .MuiDataGrid-columnHeader': {
                borderLeft: '1px solid rgba(224, 224, 224, 1)',
                '&:last-child': {
                  borderRight: 'none',
                },
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                color: '#3e3e3e'
              },
              '& .MuiDataGrid-row': {
                '&:last-child .MuiDataGrid-cell': {
                  borderBottom: '1px solid rgba(224, 224, 224, 1)',
                },
                '& .MuiDataGrid-cell:last-child': {
                  borderRight: 'none',
                },
              },
              '& .MuiDataGrid-columnSeparator': {
                display: 'none',
              },
            }}
          />
        </Box>
      </Card>
    </>
  )
}
